import {Component, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {ImageResource} from '@services/resource/image-resource';
import {StartupService} from '@services/startup.service';
import {StartupResource} from '@services/resource/startup-resource';
import {Startup} from '@services/model/startup';
import {LinkType} from '@services/resource/link-type';
import {User} from '@services/model/user';
import {VoilaUserService} from '@services/voila-user.service';
import {ImageType} from '@services/resource/image-type';
import {UploadImageComponent} from '@dashboard/upload-image/upload-image.component';
import {switchMap} from 'rxjs/operators';
import {ImageUploadService} from '@services/image-upload.service';
import {merge, Observable, Subject} from 'rxjs';
import {ContinuousActionComponent} from '@widgets/continuous-action/continuous-action.component';
import {CdkTextareaAutosize} from '@angular/cdk/text-field';
import {ClientConfig} from '@app/client-config';
import {ErrorService} from '@services/error.service';
import {ProductService} from '@services/product.service';
import {Title} from '@services/resource/title';
import {COUNTRIES, Country} from '@app/signup/countries';
import {MarkantElementsModule} from '@markant/elements-angular';
import {DropzoneComponent} from '@dashboard/dropzone/dropzone.component';
import {StartupLinkResource} from '@services/resource/startup-link-resource';
import {CommonModule} from '@angular/common';

export const STARTUP_DESC_LABEL = 'Was kannst Du über Dein Start-up erzählen?';

@Component({
  selector: 'app-startup',
  templateUrl: './startup.component.html',
  styleUrls: ['./startup.component.scss'],
  standalone: true,
  imports: [CommonModule, MarkantElementsModule, ContinuousActionComponent, DropzoneComponent, UploadImageComponent, ReactiveFormsModule],
})
export class StartupComponent implements OnInit {
  Title = Title;

  readonly PLEASE_CONTACT_MARKANT_LABEL = 'Bei Änderungswünschen wenden Sie sich bitte an den Support';
  STARTUP_DESC_LABEL = STARTUP_DESC_LABEL;

  urlRegex = '^(http:\\/\\/www\\.|https:\\/\\/www\\.|http:\\/\\/|https:\\/\\/)?[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/.*)?$';

  countries: Country[] = COUNTRIES.filter((c) => c.european).sort((c1, c2) => c1.nameDe.localeCompare(c2.nameDe));

  startup: Startup;
  logo: ImageResource | undefined;

  @ViewChild('autosize', {static: true}) autosize: CdkTextareaAutosize;

  @ViewChild(UploadImageComponent)
  logoImageUpload: UploadImageComponent;

  @ViewChild('continuousStartup', {static: true})
  continuousStartup: ContinuousActionComponent;

  @ViewChild('continuousLogo', {static: true})
  continuousLogo: ContinuousActionComponent;

  private readonly childChanges$: Subject<void> = new Subject();

  public readonly maxFileSize = ClientConfig.maxImageSize;
  public imageTypeLogo = ImageType.LOGO;
  transmittedImage: File | undefined = undefined;

  progressBarStatus = false;

  /* should mirror the editability of the product */
  editable?: boolean;

  public startupForm = new UntypedFormGroup({
    nickname: new UntypedFormControl('', []),
    description: new UntypedFormControl('', []),
    founded: new UntypedFormControl('', []),
    group: new UntypedFormControl('', []),
    website: new UntypedFormControl('', []),
    facebook: new UntypedFormControl('', []),
    twitter: new UntypedFormControl('', []),
    instagram: new UntypedFormControl('', []),
    city: new UntypedFormControl(''),
    zip: new UntypedFormControl(''),
    street: new UntypedFormControl(''),
    countryIso2Code: new UntypedFormControl(),
    contact: new UntypedFormGroup({
      title: new UntypedFormControl(''),
      email: new UntypedFormControl('', [Validators.email]),
      firstName: new UntypedFormControl(''),
      lastName: new UntypedFormControl(''),
      phone: new UntypedFormControl('', [Validators.required]),
    }),
  });

  constructor(
    private readonly errors: ErrorService,
    private readonly userService: VoilaUserService,
    private readonly startupService: StartupService,
    private readonly productService: ProductService,
    private readonly imageUploadService: ImageUploadService,
  ) {}

  get f(): any {
    return this.startupForm.controls;
  }

  ngOnInit() {
    const user = this.userService.currentUser();
    this.startupService.getStartupForUser(user.id).subscribe((startup) => this.setFormData(startup));

    /* TODO: this redirection through the product is ugly, refactor! */
    this.productService
      .productForUser(user.id)
      .pipe(switchMap((p) => this.productService.getIsEditable(p.id as string)))
      .subscribe((editable) => (this.editable = editable));

    const changes$ = merge(this.startupForm.valueChanges, this.childChanges$).pipe();
    changes$.pipe(this.continuousStartup.actions(() => this.updateStartup())).subscribe((startup) => {
      /* we monkey-patch the user here */
      const originaluser: User = this.userService.currentUser();
      const newUser: User = {...originaluser, startup: Startup.fromResource(startup)};
      this.userService.writeUserToStorage(newUser);
    });
  }

  changeLogo(img: Blob): void {
    this.deleteLogo();
    this.imageUploadService.uploadStartUpImage(this.startup.uuid as string, img, new ImageResource(ImageType.LOGO)).subscribe((imgResource) => {
      this.setLogo(imgResource);
      this.transmittedImage = undefined;
    });
  }

  deleteLogo(): void {
    this.setLogo(undefined);
    this.transmittedImage = undefined;
  }

  onImageDropped(fl: FileList): void {
    this.errors.assert(fl.length === 1, 'there should be exactly 1 dropped photo');
    this.transmittedImage = fl.item(0) as File;
  }

  private setFormData(startup: Startup) {
    this.startup = startup;
    this.logo = startup.logo;
    this.startupForm.setValue({
      nickname: startup.nickname || '',
      description: startup.description || '',
      founded: startup.founded || '',
      group: startup.productGroup || '',
      website: Startup.extractLink(LinkType.WEBSITE, startup.links),
      facebook: Startup.extractLink(LinkType.FACEBOOK, startup.links),
      twitter: Startup.extractLink(LinkType.TWITTER, startup.links),
      instagram: Startup.extractLink(LinkType.INSTAGRAM, startup.links),
      city: startup.city,
      zip: startup.zip,
      street: startup.street,
      countryIso2Code: startup.countryIso2Code,
      contact: startup.contact,
    });
  }

  private updateStartup(): Observable<StartupResource> {
    this.progressBarStatus = true;
    const links: StartupLinkResource[] = [];

    const addLink = (x: string | undefined, linkType: LinkType) => x && links.push(new StartupLinkResource(x, linkType));
    addLink(this.startupForm.controls.website.value, LinkType.WEBSITE);
    addLink(this.startupForm.controls.facebook.value, LinkType.FACEBOOK);
    addLink(this.startupForm.controls.twitter.value, LinkType.TWITTER);
    addLink(this.startupForm.controls.instagram.value, LinkType.INSTAGRAM);

    const emailControl = this.startupForm.get('contact.email') as AbstractControl;

    const startupResource: StartupResource = {
      ...this.startupForm.value,
      name: this.startup.name,
      links,
      contact: {
        ...this.startupForm.value.contact,
        /* this way we ensure that only valid email addresses get saved */
        email: emailControl.valid ? emailControl.value : this.startup.contact.email,
      },
    };
    this.progressBarStatus = false;
    return this.startupService.update(this.startup.uuid, startupResource);
  }

  private setLogo(imgResource: ImageResource | undefined): void {
    this.logo = imgResource;
    this.startup = {...this.startup, logo: imgResource};
    this.childChanges$.next();
  }
}
